import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { CONTEXT_PARAMS, URL_PARAMS } from '../../config/enrichlyParams';
import { useStoreActions } from '../../store';
import { applyThemeColors } from '../../utils/iframe/applyThemeColors';
import { IframeInbound, isStandalone, sendToParent } from '../../utils/iframe/postMessage';

/** Union of all keys the parent may push via enrichly:context, with their validators. */
const ACCEPTED_CONTEXT_KEYS: { key: string; validate?: (v: string) => boolean }[] = [
  ...URL_PARAMS.map(p => ({ key: p.contextKey, validate: p.validate })),
  ...CONTEXT_PARAMS.map(p => ({ key: p.contextKey, validate: p.validate })),
];

const IframeBridge: React.FC = () => {
  const setContext = useStoreActions(actions => actions.orgContext.setContext);
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  /**
   * How many entries the widget has pushed onto its own history since load.
   *
   * Guards against traversing past our first entry: an iframe at the start of
   * its history stack falls through to the *joint* session history, which would
   * navigate the parent page a second time — the parent already traversed once
   * before it sent us `enrichly:back`.
   */
  const historyDepthRef = useRef(0);
  const isInitialLocationRef = useRef(true);

  // `useNavigate()` is not referentially stable across renders; hold it in a ref
  // so the listener effect below can stay mounted-once and avoid re-announcing
  // `enrichly:ready` on every navigation.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (isInitialLocationRef.current) {
      isInitialLocationRef.current = false;
      return;
    }
    if (navigationType === 'PUSH') {
      historyDepthRef.current += 1;
    } else if (navigationType === 'POP') {
      historyDepthRef.current = Math.max(0, historyDepthRef.current - 1);
    }
  }, [location, navigationType]);

  useEffect(() => {
    if (isStandalone()) return;

    sendToParent({ type: 'enrichly:ready' });

    const onMessage = (event: MessageEvent) => {
      const data = event.data as IframeInbound | undefined;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'enrichly:back') {
        // Only our embedder may drive our history.
        if (event.source !== window.parent) return;
        if (historyDepthRef.current > 0) navigateRef.current(-1);
        return;
      }

      if (data.type !== 'enrichly:context' || !data.context) return;

      const partial: Record<string, string> = {};
      for (const { key, validate } of ACCEPTED_CONTEXT_KEYS) {
        const value = (data.context as Record<string, unknown>)[key];
        if (typeof value !== 'string' || value === '') continue;
        if (validate && !validate(value)) continue;
        partial[key] = value;
      }
      if (Object.keys(partial).length === 0) return;

      setContext(partial);
      applyThemeColors(partial.primaryColor, partial.textColorOverride);
    };

    window.addEventListener('message', onMessage);

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      const height = Math.ceil(entry?.contentRect?.height ?? document.body.scrollHeight);
      sendToParent({ type: 'resize', height });
    });
    observer.observe(document.body);

    return () => {
      window.removeEventListener('message', onMessage);
      observer.disconnect();
    };
  }, [setContext]);

  useEffect(() => {
    if (isStandalone()) return;
    const productHandle = location.pathname.startsWith('/product/')
      ? decodeURIComponent(location.pathname.split('/product/')[1] ?? '')
      : undefined;
    const collectionHandle = location.pathname.startsWith('/collection/')
      ? decodeURIComponent(location.pathname.split('/collection/')[1] ?? '')
      : undefined;
    sendToParent({
      type: 'navigation',
      pathname: location.pathname,
      search: location.search || undefined,
      productHandle: productHandle || undefined,
      collectionHandle: collectionHandle || undefined,
    });
  }, [location.pathname, location.search]);

  return null;
};

export default IframeBridge;
